import { useId, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { alsReistijd } from "@/features/reistijden/useReistijden";
import {
  useWedstrijdleiderKandidaten,
  useWedstrijdleiderKandidatenVoorKorps,
  type WedstrijdleiderKandidaat,
} from "./useWedstrijden";

/** Rank against an existing dag's meldadres, or a korps for one that does not exist yet (create). */
export type WedstrijdleiderBron =
  | { soort: "wedstrijddag"; wedstrijddagId: string }
  | { soort: "korps"; korpsId: string };

/**
 * A fill, not a durable link (#210): picking Beheerders here writes their names and phone numbers
 * into the free-text Wedstrijdleider/Telefoonnummer fields once, and both stay hand-editable.
 */
export function WedstrijdleiderPicker({
  bron,
  onKies,
}: {
  bron: WedstrijdleiderBron;
  onKies: (wedstrijdleider: string, telefoonnummer: string | null) => void;
}) {
  const { t } = useTranslation(["wedstrijden", "common"]);
  const id = useId();
  const [open, setOpen] = useState(false);
  // Null until the beheerder touches a checkbox: the closest ranked candidate is what renders
  // as ticked until then, a default rather than a state this popover carries between openings.
  const [aangepast, setAangepast] = useState<string[] | null>(null);

  // Both queries are called unconditionally (rules of hooks); only the one matching `bron` is
  // enabled, so only one request is ever actually made.
  const perDag = useWedstrijdleiderKandidaten(
    bron.soort === "wedstrijddag" ? bron.wedstrijddagId : "",
    open && bron.soort === "wedstrijddag",
  );
  const perKorps = useWedstrijdleiderKandidatenVoorKorps(
    bron.soort === "korps" ? bron.korpsId : "",
    open && bron.soort === "korps",
  );
  const { data: kandidaten, isLoading } = bron.soort === "wedstrijddag" ? perDag : perKorps;

  // The server already sorts closest-first, unranked last (ADR-0072); kandidaten[0] is only the
  // default when it actually has a reistijd, never an unranked candidate standing in for one.
  const dichtstbij = kandidaten?.[0]?.reistijdSeconds != null ? kandidaten[0].persoonId : undefined;
  const gekozen = aangepast ?? (dichtstbij ? [dichtstbij] : []);

  function toggle(persoonId: string) {
    setAangepast(
      gekozen.includes(persoonId)
        ? gekozen.filter((id) => id !== persoonId)
        : [...gekozen, persoonId],
    );
  }

  function toepassen() {
    const gekozenKandidaten = gekozen
      .map((id) => kandidaten?.find((k) => k.persoonId === id))
      .filter((k): k is WedstrijdleiderKandidaat => k != null);

    const telefoonnummers = gekozenKandidaten
      .map((k) => k.telefoonnummer)
      .filter((nummer): nummer is string => nummer != null);

    onKies(
      gekozenKandidaten.map((k) => k.naam).join(", "),
      telefoonnummers.length > 0 ? telefoonnummers.join(", ") : null,
    );
    setOpen(false);
  }

  return (
    <Popover
      open={open}
      onOpenChange={(volgende) => {
        setOpen(volgende);
        if (!volgende) setAangepast(null);
      }}
    >
      <PopoverTrigger asChild>
        <Button type="button" variant="outline" size="sm">
          {t("boekje.kiesWedstrijdleider")}
        </Button>
      </PopoverTrigger>

      <PopoverContent className="w-80">
        <div className="space-y-2 text-sm">
          <p className="font-medium">{t("boekje.kiesWedstrijdleiderTitel")}</p>

          {isLoading && <p className="text-muted-foreground">{t("common:actions.busy")}</p>}

          {kandidaten?.length === 0 && (
            <p className="text-muted-foreground">{t("boekje.geenBeheerders")}</p>
          )}

          {/* The create form's Dialog scroll-locks the page and doesn't know this portalled
              popover is its own scroll region, so the browser's own wheel scroll gets blocked;
              scrolling the list ourselves here works regardless. */}
          <ul
            className="max-h-64 space-y-1 overflow-y-auto"
            onWheel={(event) => {
              event.currentTarget.scrollTop += event.deltaY;
            }}
          >
            {kandidaten?.map((kandidaat) => {
              const vink = gekozen.includes(kandidaat.persoonId);
              return (
                <li key={kandidaat.persoonId} className="flex items-center gap-2">
                  <Checkbox
                    id={`${id}-${kandidaat.persoonId}`}
                    checked={vink}
                    onCheckedChange={() => toggle(kandidaat.persoonId)}
                  />
                  <Label
                    htmlFor={`${id}-${kandidaat.persoonId}`}
                    className="min-w-0 flex-1 font-normal"
                  >
                    <span className="block truncate">{kandidaat.naam}</span>
                    <span className="block truncate text-xs text-muted-foreground">
                      {kandidaat.telefoonnummer ?? t("boekje.geenTelefoonnummer")}
                    </span>
                  </Label>
                  <span className="shrink-0 text-xs text-muted-foreground">
                    {kandidaat.reistijdSeconds == null
                      ? t("boekje.reistijdOnbekend")
                      : alsReistijd(kandidaat.reistijdSeconds)}
                  </span>
                </li>
              );
            })}
          </ul>

          <div className="flex items-center justify-between pt-1">
            <Button variant="ghost" size="sm" className="h-auto p-0" onClick={() => setOpen(false)}>
              {t("common:actions.cancel")}
            </Button>
            <Button size="sm" disabled={gekozen.length === 0} onClick={toepassen}>
              {t("boekje.wedstrijdleiderInvullen")}
            </Button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
